import { useEffect, useState } from 'react'
import type { VoiceStreamStatus } from '../hooks/useVoiceStreaming'
import { getChatUiStrings } from '../utils/chatUiLocale'

type ComposerProps = {
  value: string
  placeholder: string
  listeningPlaceholder?: string
  connectingPlaceholder?: string
  onChange: (next: string) => void
  onSend: () => void
  onMicClick: () => void
  micStatus: VoiceStreamStatus
  voiceStatusHint: string
  micDisabled: boolean
  isSending: boolean
  bottomLabel: string
  sendAriaLabel?: string
  sendTitle?: string
  messageInputAriaLabel?: string
  liveTranscriptionAriaLabel?: string
  /** Temporary gTTS test: speak typed text through speakers while mic transcribes. */
  showGttsButton?: boolean
  gttsBusy?: boolean
  onGttsClick?: () => void
}

export function Composer(props: ComposerProps) {
  const ui = getChatUiStrings()
  const {
    value,
    placeholder,
    listeningPlaceholder = ui.listening,
    connectingPlaceholder = ui.connectingVoice,
    onChange,
    onSend,
    onMicClick,
    micStatus,
    voiceStatusHint,
    micDisabled,
    isSending,
    bottomLabel,
    sendAriaLabel = ui.sendMessage,
    sendTitle = ui.send,
    messageInputAriaLabel = ui.messageInput,
    liveTranscriptionAriaLabel = ui.liveTranscription,
    showGttsButton = false,
    gttsBusy = false,
    onGttsClick,
  } = props

  const micLabel =
    micStatus === 'requesting_permission'
      ? ui.micRequestingPermission
      : micStatus === 'connecting'
        ? ui.micConnecting
        : micStatus === 'recording'
          ? ui.micStopRecording
          : micStatus === 'transcribing'
            ? ui.micProcessingSpeech
            : micStatus === 'error'
              ? ui.micVoiceError
              : ui.micStartRecording

  const isVoiceSessionActive =
    micStatus === 'requesting_permission' ||
    micStatus === 'connecting' ||
    micStatus === 'recording' ||
    micStatus === 'transcribing'

  const isVoiceCapturing = micStatus === 'recording' || micStatus === 'transcribing'
  const liveTranscript = voiceStatusHint.trim()

  /** Show localized prep/listening copy as soon as the mic session starts (not only once recording). */
  const voicePlaceholder =
    micStatus === 'connecting' || micStatus === 'requesting_permission'
      ? connectingPlaceholder
      : listeningPlaceholder

  const textareaPlaceholder =
    isVoiceSessionActive && !liveTranscript ? voicePlaceholder : placeholder

  const showsListeningPlaceholder =
    (micStatus === 'recording' || micStatus === 'transcribing') && !liveTranscript

  /** Latch blue mic one frame after the listening placeholder is shown (never during connect). */
  const [micBlueLatched, setMicBlueLatched] = useState(false)
  useEffect(() => {
    if (micStatus !== 'recording' && micStatus !== 'transcribing') {
      setMicBlueLatched(false)
      return
    }
    if (liveTranscript) {
      setMicBlueLatched(true)
      return
    }
    if (!showsListeningPlaceholder || micBlueLatched) {
      return
    }
    const id = requestAnimationFrame(() => setMicBlueLatched(true))
    return () => cancelAnimationFrame(id)
  }, [micStatus, showsListeningPlaceholder, micBlueLatched, liveTranscript])

  const micShowRecordingBlue =
    micBlueLatched && (micStatus === 'recording' || micStatus === 'transcribing')

  /** Waveform beside the mic — same latch as blue mic (after listening placeholder appears). */
  const micWaveformPhase =
    micShowRecordingBlue && micStatus === 'recording'
      ? 'live'
      : micShowRecordingBlue && micStatus === 'transcribing'
        ? 'process'
        : null

  /** Live transcription in the textarea avoids an extra row that pushes the mic up. */
  const textareaValue =
    isVoiceCapturing && liveTranscript
      ? liveTranscript
      : isVoiceSessionActive && !liveTranscript
        ? ''
        : value

  return (
    <div
      className="vnw-composer"
      aria-label={ui.composerAriaLabel}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div className="vnw-composerTopRow">
        <div className="vnw-micCluster">
          <button
            type="button"
            className={
              'vnw-micBtn' + (micShowRecordingBlue ? ' vnw-micBtn--recording' : '')
            }
            disabled={micDisabled}
            aria-label={micLabel}
            title={micLabel}
            aria-pressed={micShowRecordingBlue}
            onClick={(e) => {
              if (micDisabled) return
              e.preventDefault()
              e.stopPropagation()
              onMicClick()
            }}
            onPointerDown={(e) => {
              e.stopPropagation()
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3Z"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
              <path
                d="M19 11a7 7 0 0 1-14 0"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
              <path
                d="M12 18v3"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <div className="vnw-micWaveformSlot" aria-hidden="true">
            {micWaveformPhase ? (
              <div className={`vnw-micWaveform vnw-micWaveform--${micWaveformPhase}`}>
                <span className="vnw-micWaveformBar" />
                <span className="vnw-micWaveformBar" />
                <span className="vnw-micWaveformBar" />
                <span className="vnw-micWaveformBar" />
              </div>
            ) : null}
          </div>
        </div>

        {showGttsButton ? (
          <button
            type="button"
            className="vnw-gttsBtn"
            disabled={
              gttsBusy ||
              isSending ||
              isVoiceSessionActive ||
              value.trim().length === 0
            }
            aria-label={gttsBusy ? 'gTTS lecture…' : 'Parler via gTTS et transcrire'}
            title={gttsBusy ? 'gTTS lecture…' : 'Parler via gTTS et transcrire'}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onGttsClick?.()
            }}
          >
            {gttsBusy ? (
              <span className="vnw-spinner" aria-hidden="true" />
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M11 5 6 9H2v6h4l5 4V5Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
                <path
                  d="M15.54 8.46a5 5 0 0 1 0 7.07M19.07 4.93a10 10 0 0 1 0 14.14"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </button>
        ) : null}

        <button
          type="button"
          className="vnw-sendBtn"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onSend()
          }}
          disabled={isSending || value.trim().length === 0}
          aria-label={sendAriaLabel}
          title={sendTitle}
        >
          {isSending ? (
            <span className="vnw-spinner" aria-hidden="true" />
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M22 2 11 13"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
              <path
                d="M22 2 15 22 11 13 2 9 22 2Z"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </button>
      </div>

      <textarea
        className={
          'vnw-textarea' +
          (micStatus === 'recording' && voiceStatusHint?.trim() ? ' vnw-textarea--voiceLive' : '')
        }
        value={textareaValue}
        placeholder={textareaPlaceholder}
        readOnly={isVoiceSessionActive}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            e.stopPropagation()
            onSend()
          }
        }}
        aria-label={
          isVoiceCapturing && liveTranscript ? liveTranscriptionAriaLabel : messageInputAriaLabel
        }
        aria-live={isVoiceCapturing && liveTranscript ? 'polite' : undefined}
        rows={3}
      />
      <div className="vnw-textareaBottomLabel">
        {bottomLabel}
      </div>
    </div>
  )
}
